import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';

export const callIntelResponsiveStyles = {
  page: css`
    padding: 0 !important;
  `,

  headerRoot: css`
    padding: ${theme.spacing(4, 4, 2, 4)};
    margin: 0;

    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(2)};
      position: sticky;
      top: 0px;
      background-color: ${theme.colors.white};
      z-index: ${theme.zIndex.sticky};
      &.sticky-shadow {
        box-shadow: ${theme.shadows.light};
      }
      nav {
        display: none; // Hide breadcrumbs on extra small screens
      }
    }
  `,

  headerTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: 0;
    h1 {
      font-size: ${theme.font.size.h1};
    }
    .back-icon {
      display: none;
    }

    @container (max-width: ${breakpoints.xsmall.max}px) {
      .back-icon {
        display: inline-block;
      }
      h1 {
        font-size: ${theme.font.size.h3};
      }
    }
  `,

  subtitleText: css`
    margin-top: ${theme.spacing(1)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      display: none;
    }
  `,

  filtersWrapper: css`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 4)};
    position: sticky;
    top: 0px;
    background-color: ${theme.colors.white};
    z-index: ${theme.zIndex.sticky};
    &.sticky-bottom-shadow {
      box-shadow: 0px 3px 3px 0px rgba(0, 33, 82, 0.15); // Bottom light shadow
    }
    @container (max-width: ${breakpoints.small.max}px) {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    }
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(0, 2, 2, 2)};
      position: static;
      &.sticky-bottom-shadow {
        box-shadow: none;
      }
    }
  `,

  /** Use this variant when filter wrapper only needs flex layout without grid */
  filtersWrapperFlex: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 4)};
    position: sticky;
    top: 0px;
    background-color: ${theme.colors.white};
    z-index: ${theme.zIndex.sticky};
    &.sticky-bottom-shadow {
      box-shadow: 0px 3px 3px 0px rgba(0, 33, 82, 0.15); // Bottom light shadow
    }
    @container (max-width: ${breakpoints.small.max}px) {
      flex-direction: column;
    }
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(0, 2, 2, 2)};
      position: static;
      &.sticky-bottom-shadow {
        box-shadow: none;
      }
    }
  `,

  filterBar: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,

  multiComparison: css`
    display: flex;
    align-items: center;
  `,

  contentWrapper: css`
    padding: ${theme.spacing(0, 4, 4, 4)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(0.5, 2, 2, 2)};
    }
  `,

  /** Main page header root - different from sub-view as it doesn't have back button behavior */
  mainHeaderRoot: css`
    padding: ${theme.spacing(4)};
    margin: 0;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      background-color: ${theme.colors.white};
      position: sticky;
      top: 0px;
      padding: ${theme.spacing(2)};
      z-index: ${theme.zIndex.sticky};
    }
  `,

  /** Main page header title - simpler than sub-view (no back button) */
  mainHeaderTitle: css`
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: 0;
    }
  `,

  /** Main page subtitle - hides on small screens */
  mainSubtitleText: css`
    @container (max-width: ${breakpoints.xsmall.max}px) {
      display: none;
    }
  `,

  /** Tabs bar wrapper with sticky behavior */
  tabsBarWrapper: css`
    position: sticky;
    top: 0px;
    background-color: ${theme.colors.white};
    z-index: ${theme.zIndex.sticky};
    padding: ${theme.spacing(0, 4, 1, 4)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: 0;
      top: 90px; // Height of the header in small screens
      &.sticky-shadow {
        box-shadow: ${theme.shadows.light};
      }
    }
  `,

  /** Filters wrapper with custom top offset for tabs */
  filtersWrapperWithTabs: css`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 4)};
    position: sticky;
    top: 44px;
    background-color: ${theme.colors.white};
    z-index: ${theme.zIndex.sticky};
    &.sticky-bottom-shadow {
      box-shadow: 0px 3px 3px 0px rgba(0, 33, 82, 0.15); // Bottom light shadow
    }
    @container (max-width: ${breakpoints.small.max}px) {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    }
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(2)};
      position: static;
      &.sticky-bottom-shadow {
        box-shadow: none;
      }
    }
  `,

  /** Tab panel wrapper with responsive padding */
  tabPanelWrapper: css`
    padding: ${theme.spacing(0, 4, 4, 4)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(0, 2, 2, 2)};
    }
  `,
};

export const drillDownTableSize = css`
  max-height: 620px;
`;

export const cardStyle = css`
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid ${theme.colors.neutral20};
  overflow: hidden;
  padding: ${theme.spacing(3)};
  position: relative;
`;

export const stickyTabsStyles = css`
  background-color: ${theme.colors.white};
  isolation: isolate;
  padding-bottom: ${theme.spacing(2)};
  position: sticky;
  top: 0;
  z-index: 4;

  &:before {
    background-color: ${theme.colors.white};
    content: '';
    display: block;
    height: 100%;
    left: ${theme.spacing(-3)};
    position: absolute;
    top: 0;
    width: calc(100% + ${theme.spacing(6)});
    z-index: -1;

    @media screen and (min-width: ${breakpoints.medium.min}px) {
      left: ${theme.spacing(-4)};
      width: calc(100% + ${theme.spacing(8)});
    }
  }

  &.sticky-shadow {
    &:before {
      box-shadow: ${theme.shadows.light};
    }
  }
`;

export const callIntelStyles = {
  multiComparison: css`
    flex: 1;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    @media screen and (min-width: 730px) {
      justify-content: flex-end;
    }

    > button {
      width: auto;

      .icon {
        display: inline-block;
        margin-right: ${theme.spacing(1.75)};
        width: ${theme.spacing(3)};
      }
    }
  `,
};

export const hr = css`
  border: none;
  border-top: 1px solid ${theme.colors.neutral20};
`;
